import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

interface StatusStyle {
  badge: string;
  dot: string;
  strike?: boolean;
}

/**
 * Badge de estado semántico para cotizaciones, compartido entre la lista y el detalle.
 * Paleta acordada (incidente #4/#41 + flujo de aprobación interna RF-001/RF-002):
 *   Borrador=gris, Publicada=azul, Vista=índigo, En Revisión=ámbar, Aceptada=verde,
 *   Rechazada=rojo, Expirada=naranja, Reemplazada=gris tachado,
 *   Pendiente de Aprobación=violeta (distinto del ámbar de En Revisión),
 *   Aprobada=teal (aprobación interna; distinto del verde de Aceptada del cliente).
 */
@Component({
  selector: 'app-quote-status-badge',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
      [ngClass]="style.badge">
      <span class="w-1.5 h-1.5 rounded-full shrink-0" [ngClass]="style.dot"></span>
      <span [class.line-through]="style.strike">{{ status }}</span>
    </span>
  `
})
export class QuoteStatusBadgeComponent {
  @Input() status = '';

  private static readonly STYLES: Record<string, StatusStyle> = {
    'Borrador': {
      badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
      dot: 'bg-gray-400 dark:bg-gray-500'
    },
    'Publicada': {
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      dot: 'bg-blue-500'
    },
    'Vista': {
      badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
      dot: 'bg-indigo-500'
    },
    'En Revisión': {
      badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      dot: 'bg-amber-500'
    },
    'Pendiente de Aprobación': {
      badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
      dot: 'bg-violet-500'
    },
    'Aprobada': {
      badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
      dot: 'bg-teal-500'
    },
    'Aceptada': {
      badge: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
      dot: 'bg-success-500'
    },
    'Rechazada': {
      badge: 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400',
      dot: 'bg-error-500'
    },
    'Expirada': {
      badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      dot: 'bg-orange-500'
    },
    'Reemplazada': {
      badge: 'bg-gray-100 text-gray-400 dark:bg-gray-800/60 dark:text-gray-500',
      dot: 'bg-gray-300 dark:bg-gray-600',
      strike: true
    }
  };

  private static readonly DEFAULT: StatusStyle = {
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    dot: 'bg-gray-400 dark:bg-gray-500'
  };

  get style(): StatusStyle {
    return QuoteStatusBadgeComponent.STYLES[this.status] || QuoteStatusBadgeComponent.DEFAULT;
  }
}
